import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSetAtom } from 'jotai';
import { lastListRouteAtom } from '@/lib/atoms';
import { ContactsTable } from "@/components/contacts/ContactsTable";
import { ActionBar } from "@/components/contacts/ActionBar";
import { SaveFilterDialog } from "@/components/contacts/SaveFilterDialog";
import { Button } from "@/components/ui/button";
import { ContactFilters } from "@/components/contacts/ContactFilters";
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, Filter, MoreHorizontal, Plus, Save, Share2, Upload, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useViews } from "@/hooks/useViews";
import type { SavedView } from "@/hooks/useViews";
import { recomputeRelativeDates } from "@/lib/filters/relative-dates";
import { CreateContactDialog } from "@/components/contacts/CreateContactDialog";
import { useToast } from "@/hooks/use-toast";
import { captureError } from '@/lib/sentry';
import type { Contact } from "@/types/contact";
import type { UseBulkSelectionReturn } from "@/hooks/useBulkSelection";
import type { Table } from "@tanstack/react-table";

export function FilterViewPage() {
  const { id } = useParams();
  const setLastListRoute = useSetAtom(lastListRouteAtom);
  useEffect(() => {
    if (id) setLastListRoute(`/filters/${id}`);
  }, [id, setLastListRoute]);
  const navigate = useNavigate();
  const [view, setView] = useState<SavedView | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<any>({});
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [saveAsNewOpen, setSaveAsNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const { deleteView } = useViews();
  const { toast } = useToast();

  // Selection + data-load state lifted from ContactsTable, mirroring
  // ContactsPage so ActionBar can mount with bulk-action context.
  const [selectionData, setSelectionData] = useState<{
    bulkSelection: UseBulkSelectionReturn;
    selectedContacts: Contact[];
    table: Table<Contact>;
  } | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  // Held in a ref (not state) to avoid the same render-loop ContactsPage
  // documents: useContacts returns a new invalidateCache reference whenever
  // its deps shift, and writing that into state would re-trigger the
  // data-load callback indefinitely.
  const invalidateCacheRef = useRef<(() => void) | null>(null);

  // Snapshot of the filters as loaded from the DB. Compared against
  // `currentFilters` to drive the dirty-state Save button. Stored as state
  // (not a ref) so updates to it trigger re-renders — otherwise the button
  // wouldn't hide after Save until something else nudged React to re-render
  // this component.
  const [originalFilters, setOriginalFilters] = useState<any>(null);

  const handleDataLoad = useCallback((data: { totalCount: number; invalidateCache: () => void }) => {
    setTotalCount(data.totalCount);
    invalidateCacheRef.current = data.invalidateCache;
  }, []);

  useEffect(() => {
    const fetchView = async () => {
      try {
        const { data, error } = await supabase
          .from('views')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setView(data);
        
        // Ensure query_criteria has all required fields
        const queryCriteria = (data.query_criteria || {}) as any;
        const groups = queryCriteria.groups || [];
        // Re-compute absolute dates for relative conditions (so "7 days ago" is from NOW)
        const freshGroups = recomputeRelativeDates(groups);
        const normalized = {
          ...queryCriteria,
          group_type: queryCriteria.group_type || 'AND',
          groups: freshGroups,
          sort: queryCriteria.sort || undefined,
        };
        setCurrentFilters(normalized);
        // Snapshot what we just loaded so we can detect later edits and
        // surface the inline Save button only when there's something to save.
        setOriginalFilters(normalized);

        setNewName(data.name);
      } catch (error) {
        console.error('Error fetching view:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchView();
    }
  }, [id]);

  const handleRename = async () => {
    if (!id || !newName.trim()) return;

    try {
      const { error } = await supabase
        .from('views')
        .update({ name: newName })
        .eq('id', id);

      if (error) throw error;

      setView(prev => prev ? { ...prev, name: newName } : null);
      setRenameDialogOpen(false);
      
      toast({
        description: "Filter renamed successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'filter-view-rename' });
      toast({
        title: "Error",
        description: "Failed to rename filter",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      await deleteView(id);
      toast({
        description: "Filter deleted successfully",
      });
      navigate('/filters');
    } catch (error) {
      captureError(error as Error, { feature: 'filter-view-delete' });
      toast({
        title: "Error",
        description: "Failed to delete filter",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    if (!id) return;

    const filtersToSave = {
      ...currentFilters,
      group_type: currentFilters.group_type || 'AND',
    };

    try {
      const { error } = await supabase
        .from('views')
        .update({
          query_criteria: filtersToSave,
        })
        .eq('id', id);

      if (error) throw error;

      // Reset the snapshot so the dirty-state Save button hides until the
      // user makes another edit. setOriginalFilters triggers a re-render
      // which is what actually hides the button — relying on the success
      // toast to do that would be a load-bearing accident.
      setOriginalFilters(filtersToSave);

      toast({
        description: "Filter saved successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'filter-view-save' });
      toast({
        title: "Error",
        description: "Failed to save filter",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!view) {
    return <div>Filter not found</div>;
  }

  // Check if there are active filters
  const hasActiveFilters = currentFilters.groups?.length > 0;

  // Dirty flag: true when the user has edited filters/sort since load (or
  // since the last successful save). Drives the inline Save button visibility.
  // Derived from state (not a ref) so the button hides reactively after Save
  // without depending on an external re-render trigger.
  const isFilterDirty =
    originalFilters !== null &&
    JSON.stringify(currentFilters) !== JSON.stringify(originalFilters);

  return (
    <div className="h-full flex flex-col max-w-[1400px] mx-auto w-full py-8 px-4 gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">{view.name}</h1>
          <span className="text-muted-foreground self-end mb-1">{totalCount ? `(${totalCount})` : ''}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setRenameDialogOpen(true)}>
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleDelete}
                className="text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2">
          {isFilterDirty && (
            <div className="flex">
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="rounded-r-none"
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    className="rounded-l-none border-l border-primary-foreground/20 px-2"
                    aria-label="More save options"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setSaveAsNewOpen(true)}>
                    Save as new filter…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/contacts')}
            >
              <X className="h-4 w-4 mr-2" />
              Remove Filters
            </Button>
          )}
          <div className="relative">
            <Button
              variant="outline"
              size="icon"
              className={`h-9 w-9 ${isFilterOpen ? 'bg-accent' : ''}`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <Filter className="h-4 w-4" />
            </Button>
            {hasActiveFilters && (
              <Badge
                variant="secondary"
                className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center rounded-full"
              >
                {currentFilters.groups?.length}
              </Badge>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
          >
            <Share2 className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
          >
            <Upload className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => setCreateDialogOpen(true)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Collapsible open={isFilterOpen} onOpenChange={setIsFilterOpen}>
        <CollapsibleContent className="space-y-6">
          <div className="border rounded-lg p-4 bg-card">
            <ContactFilters 
              onFilterChange={setCurrentFilters} 
              currentFilters={currentFilters}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
      
      {(hasActiveFilters || (selectionData?.bulkSelection?.effectiveCount ?? 0) > 0) && (
        <ActionBar
          totalCount={totalCount}
          appliedFilters={currentFilters}
          bulkSelection={selectionData?.bulkSelection ?? null}
          selectedContacts={selectionData?.selectedContacts ?? []}
          table={selectionData?.table ?? null}
          hasActiveFilters={!!hasActiveFilters}
          onInvalidateCache={() => invalidateCacheRef.current?.()}
        />
      )}

      <div className="flex-1 w-full">
        <ContactsTable
          filters={currentFilters}
          onDataLoad={handleDataLoad}
          onSelectionChange={setSelectionData}
        />
      </div>

      <SaveFilterDialog
        open={saveAsNewOpen}
        onOpenChange={setSaveAsNewOpen}
        currentFilters={currentFilters}
      />

      <CreateContactDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Filter</DialogTitle>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter new name"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}